/** G1 六屏的取数钩子（#4598）：只读、统一重试语义，事件流走浏览器原生 `EventSource`。 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { apiUrl } from "../api/client";
import { isRetryable } from "../api/errors";
import {
  mergeEvents,
  parseEvent,
  readAlertsLoose,
  readControlEvents,
  readDailyChain,
  readEventsHistory,
  readInboxIndex,
  readOverrides,
  readPaperState,
  readReportIndex,
  readReportView,
  readRiskLimitsRaw,
  readSimulationConfig,
  readStrategies,
  readValidationConfig,
  type EventRow,
  type KeelEvent,
} from "./g1";

const READ = {
  retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  retryDelay: 1_500,
} as const;

type R<T> = UseQueryResult<T, Error>;

function q<T>(key: readonly unknown[], fn: () => Promise<T>, extra: { refetchInterval?: number; enabled?: boolean } = {}): R<T> {
  return useQuery<T, Error>({ queryKey: key, queryFn: fn, ...READ, ...extra });
}

export const useDailyChain = (date?: string) => q(["g1", "chain", date ?? ""], () => readDailyChain(date), { refetchInterval: 60_000 });
export const useSimulationConfig = () => q(["g1", "config-simulation"], readSimulationConfig);
export const useValidationConfig = () => q(["g1", "config-validation"], readValidationConfig);
export const useRiskLimitsRaw = () => q(["g1", "risk-limits-raw"], readRiskLimitsRaw);
export const useStrategies = () => q(["g1", "strategies"], readStrategies);
export const useInboxIndex = () => q(["g1", "inbox-index"], readInboxIndex);
export const useReportIndex = () => q(["g1", "report-index"], readReportIndex, { refetchInterval: 60_000 });
export const useReportView = (date: string | null) =>
  q(["g1", "report", date ?? ""], () => readReportView(date as string), { enabled: Boolean(date) });
export const useAlertsLoose = (date?: string) => q(["g1", "alerts", date ?? ""], () => readAlertsLoose(date));
export const usePaperState = () => q(["g1", "paper-state"], readPaperState, { refetchInterval: 10_000 });
export const useControlEvents = () => q(["g1", "control-events"], readControlEvents);
export const useOverrides = () => q(["g1", "overrides"], readOverrides);
export const useEventsHistory = (params: { level?: string; source?: string; before_seq?: number | null; limit?: number }) =>
  q(["g1", "events-history", params], () => readEventsHistory(params));

export type StreamState = "connecting" | "open" | "reconnecting";

/** 订阅 `GET /events`（SSE）。断线由浏览器 `EventSource` 自动重连并带 `Last-Event-ID` 补播；
 * 补播出现 seq 缺口时 `mergeEvents` 插一行缺口提示，不静默。 */
export function useEventStream(max = 2000): { rows: EventRow[]; state: StreamState; lastSeq: number | null; error: string | null } {
  const [rows, setRows] = useState<EventRow[]>([]);
  const [state, setState] = useState<StreamState>("connecting");
  const [error, setError] = useState<string | null>(null);
  const lastSeq = useRef<number | null>(null);

  useEffect(() => {
    if (typeof EventSource === "undefined") {
      setError("浏览器不支持 EventSource");
      return undefined;
    }
    const source = new EventSource(apiUrl("/events"));
    const onEvent = (message: MessageEvent<string>) => {
      try {
        const event: KeelEvent = parseEvent(message.data);
        lastSeq.current = Math.max(lastSeq.current ?? 0, event.seq);
        setRows((current) => mergeEvents(current, [event], max));
        setError(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    };
    const types = ["signal", "order", "fill", "recon", "halt", "recover", "run"];
    for (const type of types) source.addEventListener(type, onEvent as EventListener);
    source.onmessage = onEvent;
    source.onopen = () => setState("open");
    source.onerror = () => setState("reconnecting");
    return () => {
      for (const type of types) source.removeEventListener(type, onEvent as EventListener);
      source.close();
    };
  }, [max]);

  return { rows, state, lastSeq: lastSeq.current, error };
}
